import { IN_TRAY } from '../model/areas.js';
import { port, powerIn } from '../catalog/ports.js';
import { makeItem } from '../model/board.js';
import type { BoardData, Connection, Item, Port, RfSpec } from '../model/schema.js';

/** Which end of an in-ear link an IEM item is: the transmitter (RF out) or the body pack. */
export type IemRole = 'transmitter' | 'bodypack';

function rfPort(item: Item, direction: Port['direction']): Port | undefined {
  return item.ports?.find((p) => p.signal === 'wireless' && p.direction === direction);
}

export function iemRole(item: Item): IemRole | undefined {
  if (item.kind !== 'iem') return undefined;
  return rfPort(item, 'out') === undefined ? 'bodypack' : 'transmitter';
}

/** Items connected to `item` over RF ports (transmitter ↔ body pack / receiver). */
export function rfPartners(data: Pick<BoardData, 'items' | 'connections'>, item: Item): Item[] {
  const rf = new Set((item.ports ?? []).filter((p) => p.signal === 'wireless').map((p) => p.id));
  const out: Item[] = [];
  for (const c of Object.values(data.connections)) {
    const [mine, other] =
      c.from.itemId === item.id ? [c.from, c.to] : c.to.itemId === item.id ? [c.to, c.from] : [];
    if (mine === undefined || !rf.has(mine.portId)) continue;
    const partner = data.items[other!.itemId];
    if (partner !== undefined && !out.includes(partner)) out.push(partner);
  }
  return out;
}

export interface TransmitterPlan {
  transmitter: Item;
  connection: Connection;
  /** The body pack with an RF input and the wireless link. */
  bodypack: Item;
}

/**
 * A wireless IEM is two objects (OIL-52): the transmitter at the monitor desk gets the cable
 * (stereo line in), the body pack at the musician receives over RF. The transmitter starts
 * unplaced and shares band / frequency with the pack.
 */
export function planIemTransmitter(
  bodypack: Item,
  ids: { transmitter: string; connection: string },
  label: string,
): TransmitterPlan {
  const rf: RfSpec = { ...bodypack.rf, link: 'wireless' };
  const ports = bodypack.ports ?? [];
  let packRf = rfPort(bodypack, 'in');
  const nextPorts = [...ports];
  if (packRf === undefined) {
    packRf = port(
      ports.some((p) => p.id === 'rf') ? 'rf-in' : 'rf',
      'RF',
      'in',
      'wireless',
      'wireless',
    );
    nextPorts.push(packRf);
  }
  const transmitter = makeItem('iem', {
    id: ids.transmitter,
    layerId: bodypack.layerId,
    label,
    ...IN_TRAY,
    ...(bodypack.group !== undefined ? { group: bodypack.group } : {}),
    ports: [
      port('inl', 'In L', 'in', 'line', 'xlr3'),
      port('inr', 'In R', 'in', 'line', 'xlr3'),
      port('rf', 'RF', 'out', 'wireless', 'wireless'),
      powerIn('iec'),
    ],
    rf,
  });
  return {
    transmitter,
    connection: {
      id: ids.connection,
      from: { itemId: transmitter.id, portId: 'rf' },
      to: { itemId: bodypack.id, portId: packRf.id },
    },
    bodypack: { ...bodypack, ports: nextPorts, rf },
  };
}

/**
 * Sets the RF spec of an item and of its RF partners – a transmitter and its body pack share one
 * link. `undefined` removes it. Returns the changed items.
 */
export function withRf(
  data: Pick<BoardData, 'items' | 'connections'>,
  item: Item,
  rf: RfSpec | undefined,
): Item[] {
  const set = (i: Item): Item => {
    const { rf: _old, ...rest } = i;
    return rf === undefined ? rest : { ...rest, rf };
  };
  return [set(item), ...rfPartners(data, item).map(set)];
}
